import { FormEvent, useContext, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CssBaseline from "@mui/material/CssBaseline";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import AuthContext from "../../store/auth-context";
import classes from "./SignInSide.module.css";
import proteaO from "../UI/protea-o-suave-preview.png";
import PartnerLogos from "../UI/PartnerLogos";
import MarineJellyfish from "./MarineJellyfish";
import LandingFish from "../LandingFish";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import GuardianesInvitation from "../GuardianesInvitation";
import { useGuardianesDiscovery } from "../../hooks/useGuardianesDiscovery";
import { markGuardianesDiscovered } from "../../guardianes/guardianesStorage";
import { trackAnalyticsEvent } from "../../services/analytics";

const baseUrl = process.env.REACT_APP_API_HOST;

function Copyright() {
  return (
    <Typography className={classes.copyright} component="p">
      © {new Date().getFullYear()} Protea ·{" "}
      <Link color="inherit" href="https://pyxis.tech">
        Pyxis
      </Link>
    </Typography>
  );
}

function ProteaMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${classes.wordmark} ${compact ? classes.compact : ""}`} aria-label="Protea">
      <span className={classes.wordmarkText}>
        <span className={classes.wordmarkName} aria-hidden="true">
          <span>PR</span>
          <span className={classes.wordmarkMark}>
            <img src={proteaO} alt="" />
          </span>
          <span>TEA</span>
        </span>
        <span className={classes.wordmarkTagline}>observatorio ambiental</span>
      </span>
    </div>
  );
}

const theme = createTheme({
  palette: {
    primary: { main: "#0b7d87" },
    secondary: { main: "#18a793" },
  },
  typography: {
    fontFamily: "Inter, Avenir Next, Segoe UI, sans-serif",
  },
});

const humanLoginError = (status: number) => {
  if (status === 400 || status === 401 || status === 403) {
    return "El correo o la contraseña no coinciden. Revisalos y probá de nuevo.";
  }
  if (status === 429) {
    return "Hubo varios intentos seguidos. Esperá un cachito y probá de nuevo.";
  }
  if (status >= 500) {
    return "Protea está teniendo un inconveniente. Probá de nuevo en un ratito.";
  }
  return "No pudimos abrir tu sesión. Probá de nuevo.";
};

const connectionError = "No pudimos conectarnos con Protea. Revisá tu conexión e intentá de nuevo.";

export default function SignInSide() {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [invitationOpen, setInvitationOpen] = useState(false);
  const guardianesDiscovered = useGuardianesDiscovery();

  const handleGuardianesActivation = () => {
    if (guardianesDiscovered) {
      navigate("/guardianes-de-protea");
      return;
    }
    markGuardianesDiscovered();
    trackAnalyticsEvent("guardianes_discovered");
    setInvitationOpen(true);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    const data = new FormData(event.currentTarget);
    const enteredEmail = String(data.get("email") ?? "").trim();
    const enteredPassword = String(data.get("password") ?? "");

    if (!enteredEmail || !enteredPassword) {
      setErrorMessage("Completá tu correo y tu contraseña para entrar.");
      return;
    }

    const formBody = new URLSearchParams({
      username: enteredEmail,
      password: enteredPassword,
    });

    setIsLoading(true);
    try {
      const res = await fetch(`${baseUrl}/auth/login`, {
        method: "POST",
        body: formBody,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
      });
      if (!res.ok) {
        throw new Error(humanLoginError(res.status));
      }
      const response = await res.json();
      const expirationMinutes = response?.expiresIn ? response.expiresIn / 60 : 360;
      const expirationTime = new Date(Date.now() + expirationMinutes * 1000 * 60);
      authCtx.login(response.access_token, expirationTime.toISOString(), enteredEmail);
      navigate("/dashboard");
    } catch (error) {
      setErrorMessage(error instanceof TypeError ? connectionError : (
        error instanceof Error ? error.message : "No pudimos abrir tu sesión. Probá de nuevo."
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnonymousLogin = async () => {
    setErrorMessage(null);
    setIsLoading(true);
    try {
      const res = await fetch(`${baseUrl}/auth/anonymous`, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
      });
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error("El acceso como visitante no está disponible por ahora.");
        }
        throw new Error("No pudimos abrir el acceso como visitante. Probá de nuevo.");
      }
      const response = await res.json();
      const expirationMinutes = response?.expiresIn ? response.expiresIn / 60 : 360;
      const expirationTime = new Date(Date.now() + expirationMinutes * 1000 * 60);
      authCtx.login(
        response.access_token,
        expirationTime.toISOString(),
        response.userEmail || response.displayName
      );
      navigate("/dashboard");
    } catch (error) {
      setErrorMessage(error instanceof TypeError ? connectionError : (
        error instanceof Error ? error.message : "No pudimos abrir el acceso como visitante."
      ));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <main className={classes.page}>
        <section className={classes.storyPanel} aria-labelledby="landing-title">
          <div className={classes.storyContent}>
            <ProteaMark />
            <span className={classes.eyebrow}>Tecnología para cuidar la costa</span>
            <h1 id="landing-title" className={classes.storyTitle}>
              Mirar el mar también es cuidarlo.
            </h1>
            <p className={classes.storyText}>
              Protea convierte señales ambientales en decisiones claras para entender,
              anticipar y proteger nuestros ecosistemas.
            </p>
            <p className={classes.guardianesHint}>
              ¿Nos ayudás a encontrar a Protea, nuestra mascota medusa, para que nos enseñe sobre el mar jugando?
            </p>
            <div className={classes.storyMeta} aria-label="Capacidades de Protea">
              <span className={classes.metaPill}>Datos en tiempo real</span>
              <span className={classes.metaPill}>Alertas inteligentes</span>
              <span className={classes.metaPill}>Memoria ambiental</span>
            </div>
            <PartnerLogos className={classes.partnerLogos} label="Powered by" />
          </div>

          <div className={classes.oceanScene}>
            <svg className={`${classes.wave} ${classes.waveBack}`} viewBox="0 0 1000 260" preserveAspectRatio="none">
              <path fill="currentColor" d="M0 128c115-60 205 42 320-4 128-51 192-47 310 5 128 56 210-56 370-4v135H0Z" />
            </svg>
            <svg className={`${classes.wave} ${classes.waveMid}`} viewBox="0 0 1000 260" preserveAspectRatio="none">
              <path fill="currentColor" d="M0 156c105-45 190 17 290-18 128-44 225 76 350 23 138-58 220-25 360 16v83H0Z" />
            </svg>
            <svg className={`${classes.wave} ${classes.waveFront}`} viewBox="0 0 1000 260" preserveAspectRatio="none">
              <path fill="currentColor" d="M0 190c120-48 205 35 325-4 104-34 190 41 298 8 150-46 229 2 377-22v88H0Z" />
            </svg>
            <span className={`${classes.bubble} ${classes.bubbleOne}`} />
            <span className={`${classes.bubble} ${classes.bubbleTwo}`} />
            <span className={`${classes.bubble} ${classes.bubbleThree}`} />
            <span className={`${classes.bubble} ${classes.bubbleFour}`} />
            <div className={classes.marineLife} aria-hidden="true">
              <LandingFish />
            </div>
            <div className={classes.starFish}>
              <svg viewBox="0 0 80 80" role="presentation">
                <path d="M40 7c7 0 7 14 11 19 6-2 18-9 22-3 4 6-8 14-12 19 5 5 16 14 11 20-5 6-17-3-23-5-3 6-4 20-11 20s-8-14-11-20c-6 2-18 11-23 5-5-6 6-15 11-20-4-5-16-13-12-19 4-6 16 1 22 3 4-5 4-19 11-19Z" />
                <path d="M40 34c4 0 7 3 7 7s-3 7-7 7-7-3-7-7 3-7 7-7Z" />
                <circle cx="40" cy="37" r="1.2" fill="currentColor" stroke="none" />
              </svg>
            </div>
          </div>
          <MarineJellyfish interactive onActivate={handleGuardianesActivation} />
          <div className={classes.careNote}>monitoreo · cuidado · futuro</div>
        </section>

        <section className={classes.authPanel} aria-label="Acceso a Protea">
          <div className={classes.authContent}>
            <div className={classes.mobileBrand}><ProteaMark compact /></div>
            <div className={classes.authKicker}><span className={classes.statusDot} /> Portal Protea</div>
            <h2 className={classes.authTitle}>¡Qué alegría tenerte de vuelta!</h2>
            <p className={classes.authIntro}>
              Elegí cómo querés entrar al observatorio.
            </p>
            {errorMessage && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
            <div className={classes.authOption}>
              <span className={classes.optionEyebrow}>CUENTA PROTEA</span>
              <h3 className={classes.optionTitle}>Ingresá con tu usuario</h3>
              <p className={classes.optionText}>
                Usá tu correo y contraseña para acceder a tu cuenta y configuración.
              </p>
            </div>
            <Box component="form" noValidate onSubmit={handleSubmit} className={classes.form}>
              <TextField
                required
                fullWidth
                id="email"
                label="Correo electrónico"
                name="email"
                autoComplete="email"
                autoFocus
              />
              <TextField
                required
                fullWidth
                name="password"
                label="Contraseña"
                type="password"
                id="password"
                autoComplete="current-password"
              />
              <FormControlLabel
                className={classes.remember}
                control={<Checkbox value="remember" color="primary" />}
                label="Recordarme"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                className={classes.submitButton}
                disabled={isLoading}
              >
                {isLoading ? "Ingresando…" : "Ingresar al observatorio"}
              </Button>
            </Box>
            <div className={classes.visitorOption}>
              <span className={classes.optionEyebrow}>ACCESO TEMPORAL</span>
              <h3 className={classes.optionTitle}>Explorá como visitante</h3>
              <p className={classes.optionText}>
                Entrá sin registrarte. Este acceso es anónimo y dura hasta seis horas.
              </p>
              <Button
                type="button"
                fullWidth
                variant="outlined"
                className={classes.anonymousButton}
                onClick={handleAnonymousLogin}
                disabled={isLoading}
              >
                {isLoading ? "Creando acceso…" : "Entrar como visitante"}
              </Button>
            </div>
            <div className={classes.secondaryOptions} aria-label="Otras opciones de acceso">
              <Link href="#recuperar-acceso" className={classes.secondaryOption}>
                <span className={classes.secondaryIcon} aria-hidden="true">
                  <LockResetRoundedIcon />
                </span>
                <span className={classes.secondaryOptionContent}>
                  <span className={classes.secondaryOptionEyebrow}>RECUPERAR ACCESO</span>
                  <strong className={classes.secondaryOptionTitle}>¿Olvidaste tu contraseña?</strong>
                  <span className={classes.secondaryOptionText}>Solicitá ayuda para volver a ingresar.</span>
                </span>
              </Link>
              <Link component={RouterLink} to="/register" className={classes.secondaryOption}>
                <span className={classes.secondaryIcon} aria-hidden="true">
                  <PersonAddAlt1RoundedIcon />
                </span>
                <span className={classes.secondaryOptionContent}>
                  <span className={classes.secondaryOptionEyebrow}>NUEVA CUENTA</span>
                  <strong className={classes.secondaryOptionTitle}>Crear una cuenta</strong>
                  <span className={classes.secondaryOptionText}>Sumate al observatorio y guardá tu configuración.</span>
                </span>
              </Link>
            </div>
            <Copyright />
          </div>
        </section>
      </main>
      <GuardianesInvitation open={invitationOpen} onClose={() => setInvitationOpen(false)} />
    </ThemeProvider>
  );
}
